// Online play (standard 1.2, plan.md §19): constants, rate limits, the host-to-host wire format on the WebRTC data
// channels, and validators for every message that crosses a trust boundary (room server <-> page, peer <-> peer).
// No DOM and no WebRTC here, so it is shared by the host, the room server (worker/room.ts) and the unit tests.

/** Limits of standard 1.2 (spec §4.12). */
export const NET = {
  maxPlayers: 4,
  /** Largest game packet on channel 0 (unreliable, unordered). */
  unreliableMax: 2048,
  /** Largest game packet on channel 1 (reliable, ordered). */
  reliableMax: 65536,
  /** Per peer and direction: channel 0. */
  unreliableRate: { packets: 500, bytes: 512 * 1024 },
  /** Per peer and direction: channel 1. */
  reliableRate: { packets: 250, bytes: 1024 * 1024 },
  /** Channel 1 refuses sends while this much is still queued for one peer. */
  reliableBuffered: 4 * 1024 * 1024,
  /** Packets the host queues for a module that has not started yet (per session). */
  queueMax: 512,
  pingMs: 500,
  /** No traffic from a peer for this long: state 2 (silent); for goneMs: state 0 (gone). */
  silentMs: 3000,
  goneMs: 15000,
  /** Room lifetime (the room server closes it after this, started or not). */
  roomTtlMs: 10 * 60 * 1000,
  /** Largest room-server (signalling) message. */
  signalMax: 16 * 1024,
  /** Largest native save carried by a session boot. */
  saveMax: 1 << 20,
} as const;

/** rtw_net_peer states. */
export const PEER_GONE = 0;
export const PEER_CONNECTED = 1;
export const PEER_SILENT = 2;

/** rtw_net_stat kinds. */
export const STAT_RTT = 0; // live smoothed round trip to `peer`, ms
export const STAT_MEDIAN = 1; // median round trip me <-> peer measured in the room before the boot (same on both sides)
export const STAT_P90 = 2; // 90th percentile of the same samples (same on both sides)
export const STAT_WORST = 3; // the largest STAT_P90 over every pair of the session (same on every peer)

/** rtw_net_send results. */
export const SEND_OK = 0;
export const SEND_REFUSED = -1; // too big, over the rate limit, bad channel, too much queued
export const SEND_NO_PEER = -2; // no session, or that peer is not (or no longer) in it

export type NetModel = 'lockstep' | 'link';

export const ROOM_ALPHABET = 'ACDEFHJKMNPQRTUVWXY3479';
export const ROOM_CODE = /^[ACDEFHJKMNPQRTUVWXY3479]{6}$/;

export function newRoomCode(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  let s = '';
  while (s.length < 6) {
    for (const b of random(12)) {
      if (b < 230 && s.length < 6) s += ROOM_ALPHABET[b % 23]; // 230 = 10 * 23: no modulo bias
    }
  }
  return s;
}

/** Normalise what a player typed as a room code (case, spaces, look-alikes); null if it can't be one. */
export function cleanRoomCode(s: string): string | null {
  const c = s.toUpperCase().replace(/[\s-]/g, '').replace(/0/g, 'Q').replace(/O/g, 'Q').replace(/[IL1]/g, 'T');
  return ROOM_CODE.test(c) ? c : null;
}

// ---- rate limits -------------------------------------------------------------------------------------------

/** A token bucket refilled continuously at `rate` per second, holding at most one second's worth. */
export class TokenBucket {
  private tokens: number;
  private at: number;
  readonly rate: number;
  constructor(rate: number, now: number) { this.rate = rate; this.tokens = rate; this.at = now; }
  /** Refill up to `now` and say whether `n` tokens are there (nothing is taken). */
  has(n: number, now: number): boolean {
    this.tokens = Math.min(this.rate, this.tokens + (Math.max(0, now - this.at) / 1000) * this.rate);
    this.at = Math.max(this.at, now);
    return this.tokens >= n;
  }
  take(n: number, now: number): boolean {
    if (!this.has(n, now)) return false;
    this.tokens -= n;
    return true;
  }
}

/** Packets and bytes per second for one peer, one channel and one direction. */
export class RateLimit {
  private p: TokenBucket;
  private b: TokenBucket;
  constructor(limit: { packets: number; bytes: number }, now: number) {
    this.p = new TokenBucket(limit.packets, now);
    this.b = new TokenBucket(limit.bytes, now);
  }
  allow(len: number, now: number): boolean {
    if (!this.p.has(1, now) || !this.b.has(len, now)) return false;
    this.p.take(1, now);
    this.b.take(len, now);
    return true;
  }
}

export function channelMax(ch: number): number {
  return ch === 0 ? NET.unreliableMax : ch === 1 ? NET.reliableMax : -1;
}

// ---- wire format between two host pages (data channels "u" and "r") --------------------------------------
//
//   [0, ...payload]                 a game packet, sent directly
//   [1, to, ...inner]               any other message (an encoded Wire) for peer `to`, sent to the room host to relay
//   [2, from, ...inner]             a message relayed by the room host from peer `from` (never nested)
//   [3, f64 t]                      ping (channel "u" only)
//   [4, f64 t]                      pong: the ping's t echoed
//   [5, ...utf-8 JSON]              control message (channel "r" only), see checkCtl
//   [6, u32 offset, u32 total, ...] a chunk of the session boot's save (channel "r" only)
// The channel itself says reliable or not: a game packet on "u" is channel 0, on "r" channel 1.

export const W_DATA = 0, W_RELAY_TO = 1, W_RELAY_FROM = 2, W_PING = 3, W_PONG = 4, W_CTL = 5, W_BLOB = 6;
export const BLOB_CHUNK = 32 * 1024;

export type Wire =
  | { k: 'data'; payload: Uint8Array }
  | { k: 'relay-to'; to: number; inner: Uint8Array }
  | { k: 'relay-from'; from: number; inner: Uint8Array }
  | { k: 'ping'; t: number }
  | { k: 'pong'; t: number }
  | { k: 'ctl'; msg: Ctl }
  | { k: 'blob'; offset: number; total: number; bytes: Uint8Array };

function withHead(head: number[], payload: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(head.length + payload.length);
  out.set(head);
  out.set(payload, head.length);
  return out;
}

export function encodeWire(w: Wire): Uint8Array<ArrayBuffer> {
  switch (w.k) {
    case 'data': return withHead([W_DATA], w.payload);
    case 'relay-to': return withHead([W_RELAY_TO, w.to], w.inner);
    case 'relay-from': return withHead([W_RELAY_FROM, w.from], w.inner);
    case 'ping': case 'pong': {
      const b = new Uint8Array(9);
      b[0] = w.k === 'ping' ? W_PING : W_PONG;
      new DataView(b.buffer).setFloat64(1, w.t, true);
      return b;
    }
    case 'ctl': return withHead([W_CTL], new TextEncoder().encode(JSON.stringify(w.msg)));
    case 'blob': {
      const h = new Uint8Array(9);
      h[0] = W_BLOB;
      const v = new DataView(h.buffer);
      v.setUint32(1, w.offset, true);
      v.setUint32(5, w.total, true);
      return withHead([...h], w.bytes);
    }
  }
}

/** Decode one data-channel message; null if malformed. `reliable`: it came on channel "r". */
export function decodeWire(b: Uint8Array, reliable: boolean): Wire | null {
  if (b.length < 1) return null;
  const max = (reliable ? NET.reliableMax : NET.unreliableMax);
  switch (b[0]) {
    case W_DATA: return b.length - 1 <= max ? { k: 'data', payload: b.subarray(1) } : null;
    case W_RELAY_TO: case W_RELAY_FROM: {
      // the inner message must itself be valid and not another relay
      if (b.length < 3 || b[2] === W_RELAY_TO || b[2] === W_RELAY_FROM) return null;
      const inner = b.subarray(2);
      if (!decodeWire(inner, reliable)) return null;
      return b[0] === W_RELAY_TO ? { k: 'relay-to', to: b[1], inner } : { k: 'relay-from', from: b[1], inner };
    }
    case W_PING: case W_PONG: {
      if (reliable || b.length !== 9) return null;
      const t = new DataView(b.buffer, b.byteOffset, 9).getFloat64(1, true);
      return Number.isFinite(t) ? { k: b[0] === W_PING ? 'ping' : 'pong', t } : null;
    }
    case W_CTL: {
      if (!reliable || b.length > NET.signalMax) return null;
      try {
        const msg = checkCtl(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(b.subarray(1))));
        return msg ? { k: 'ctl', msg } : null;
      } catch { return null; }
    }
    case W_BLOB: {
      if (!reliable || b.length < 9) return null;
      const v = new DataView(b.buffer, b.byteOffset, 9);
      const offset = v.getUint32(1, true), total = v.getUint32(5, true);
      const bytes = b.subarray(9);
      if (total > NET.saveMax || offset + bytes.length > total || bytes.length > BLOB_CHUNK) return null;
      return { k: 'blob', offset, total, bytes };
    }
    default: return null;
  }
}

// ---- control messages (peer <-> peer, JSON on channel "r") ------------------------------------------------

/** Round-trip samples to other peers (by room id): [id, median ms, p90 ms]. */
export type PingRow = [number, number, number];

export type Ctl =
  // first message on every link: both sides check the build id and the ROM's SHA-1
  | { t: 'hello'; v: 1; build: string; rom: string }
  // a guest's measured round trips to the others, sent to the room host while in the room
  | { t: 'pings'; rows: PingRow[] }
  // the room host starts the session. ids: the room ids in session order (index 0 = room host).
  // lockstep: options and `save` bytes (sent next as blob chunks) are what everyone boots with
  | { t: 'boot'; model: NetModel; ids: number[]; options: [number, number][]; save: number; pairs: [number, number, number, number][] }
  | { t: 'vis'; hidden: boolean }
  | { t: 'bye' };

const isInt = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const HEX64 = /^[0-9a-f]{64}$/;
const HEX40 = /^[0-9a-f]{40}$/;

export function checkCtl(d: unknown): Ctl | null {
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  switch (m.t) {
    case 'hello':
      return m.v === 1 && typeof m.build === 'string' && HEX64.test(m.build) && typeof m.rom === 'string' && HEX40.test(m.rom)
        ? { t: 'hello', v: 1, build: m.build, rom: m.rom } : null;
    case 'pings': {
      if (!Array.isArray(m.rows) || m.rows.length > 16) return null;
      const rows = m.rows.filter((r): r is PingRow => Array.isArray(r) && r.length === 3 && isInt(r[0], 0, 255) && isInt(r[1], 0, 60000) && isInt(r[2], 0, 60000));
      return rows.length === m.rows.length ? { t: 'pings', rows } : null;
    }
    case 'boot': {
      if ((m.model !== 'lockstep' && m.model !== 'link') || !Array.isArray(m.ids) || m.ids.length < 2 || m.ids.length > NET.maxPlayers) return null;
      if (!m.ids.every((x) => isInt(x, 0, 255)) || new Set(m.ids).size !== m.ids.length) return null;
      if (!Array.isArray(m.options) || m.options.length > 256 || !m.options.every((o) => Array.isArray(o) && o.length === 2 && isInt(o[0], -0x80000000, 0x7fffffff) && isInt(o[1], -0x80000000, 0x7fffffff))) return null;
      if (!isInt(m.save, 0, NET.saveMax)) return null;
      if (!Array.isArray(m.pairs) || m.pairs.length > 16 || !m.pairs.every((p) => Array.isArray(p) && p.length === 4 && isInt(p[0], 0, 255) && isInt(p[1], 0, 255) && isInt(p[2], 0, 60000) && isInt(p[3], 0, 60000))) return null;
      return { t: 'boot', model: m.model, ids: m.ids as number[], options: m.options as [number, number][], save: m.save, pairs: m.pairs as [number, number, number, number][] };
    }
    case 'vis': return typeof m.hidden === 'boolean' ? { t: 'vis', hidden: m.hidden } : null;
    case 'bye': return { t: 'bye' };
    default: return null;
  }
}

// ---- room server (signalling) messages -------------------------------------------------------------------

/** What travels through the room server between two pages: WebRTC session descriptions and ICE candidates. */
export type Signal =
  | { sdp: { type: 'offer' | 'answer'; sdp: string } }
  | { ice: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null } };

export type RoomRefusal = 'notfound' | 'closed' | 'full' | 'build' | 'rom' | 'bad' | 'busy';
export type RoomEnd = 'started' | 'expired' | 'host-left';

export type ToRoom =
  // first message after {t:'room'}: the match keys (see matchKeys); the creator also says how many players fit
  | { t: 'hello'; build: string; rom: string; max?: number }
  | { t: 'signal'; to: number; data: Signal }
  | { t: 'start' } // room host: the session starts, close the room
  | { t: 'leave' };

export type FromRoom =
  | { t: 'room'; code: string }
  | { t: 'welcome'; you: number; host: number; max: number; peers: number[]; expires: number }
  | { t: 'join'; id: number }
  | { t: 'left'; id: number }
  | { t: 'signal'; from: number; data: Signal }
  | { t: 'refused'; reason: RoomRefusal }
  | { t: 'closed'; reason: RoomEnd };

function checkSignal(d: unknown): Signal | null {
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  if (m.sdp && typeof m.sdp === 'object') {
    const s = m.sdp as Record<string, unknown>;
    return (s.type === 'offer' || s.type === 'answer') && typeof s.sdp === 'string' && s.sdp.length <= 12000
      ? { sdp: { type: s.type, sdp: s.sdp } } : null;
  }
  if (m.ice && typeof m.ice === 'object') {
    const c = m.ice as Record<string, unknown>;
    if (typeof c.candidate !== 'string' || c.candidate.length > 1024) return null;
    const mid = c.sdpMid === null || c.sdpMid === undefined ? null : typeof c.sdpMid === 'string' && c.sdpMid.length <= 64 ? c.sdpMid : undefined;
    const idx = c.sdpMLineIndex === null || c.sdpMLineIndex === undefined ? null : isInt(c.sdpMLineIndex, 0, 64) ? c.sdpMLineIndex : undefined;
    if (mid === undefined || idx === undefined) return null;
    return { ice: { candidate: c.candidate, sdpMid: mid, sdpMLineIndex: idx } };
  }
  return null;
}

/** A message from a page, as the room server sees it. */
export function checkToRoom(text: unknown): ToRoom | null {
  if (typeof text !== 'string' || text.length > NET.signalMax) return null;
  let d: unknown;
  try { d = JSON.parse(text); } catch { return null; }
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  switch (m.t) {
    case 'hello':
      if (typeof m.build !== 'string' || !HEX64.test(m.build) || typeof m.rom !== 'string' || !HEX64.test(m.rom)) return null;
      if (m.max !== undefined && !isInt(m.max, 2, NET.maxPlayers)) return null;
      return { t: 'hello', build: m.build, rom: m.rom, ...(m.max !== undefined ? { max: m.max as number } : {}) };
    case 'signal': {
      const data = checkSignal(m.data);
      return isInt(m.to, 0, 255) && data ? { t: 'signal', to: m.to, data } : null;
    }
    case 'start': return { t: 'start' };
    case 'leave': return { t: 'leave' };
    default: return null;
  }
}

const REFUSALS: RoomRefusal[] = ['notfound', 'closed', 'full', 'build', 'rom', 'bad', 'busy'];
const ENDS: RoomEnd[] = ['started', 'expired', 'host-left'];

/** A message from the room server, as the page sees it. */
export function checkFromRoom(text: unknown): FromRoom | null {
  if (typeof text !== 'string' || text.length > NET.signalMax + 256) return null;
  let d: unknown;
  try { d = JSON.parse(text); } catch { return null; }
  if (!d || typeof d !== 'object') return null;
  const m = d as Record<string, unknown>;
  switch (m.t) {
    case 'room': return typeof m.code === 'string' && ROOM_CODE.test(m.code) ? { t: 'room', code: m.code } : null;
    case 'welcome':
      return isInt(m.you, 0, 255) && isInt(m.host, 0, 255) && isInt(m.max, 2, NET.maxPlayers) && Array.isArray(m.peers)
        && m.peers.length <= NET.maxPlayers && m.peers.every((p) => isInt(p, 0, 255)) && typeof m.expires === 'number'
        ? { t: 'welcome', you: m.you, host: m.host, max: m.max, peers: m.peers as number[], expires: m.expires } : null;
    case 'join': case 'left': return isInt(m.id, 0, 255) ? { t: m.t, id: m.id } : null;
    case 'signal': { const data = checkSignal(m.data); return isInt(m.from, 0, 255) && data ? { t: 'signal', from: m.from, data } : null; }
    case 'refused': return REFUSALS.includes(m.reason as RoomRefusal) ? { t: 'refused', reason: m.reason as RoomRefusal } : null;
    case 'closed': return ENDS.includes(m.reason as RoomEnd) ? { t: 'closed', reason: m.reason as RoomEnd } : null;
    default: return null;
  }
}

/**
 * What a page tells the room server so it can refuse a different build or ROM: SHA-256 of the room code with the
 * build id and with the ROM's SHA-1. The server learns neither which game nor which ROM; peers compare the plain
 * hashes again over their own connection (Ctl hello).
 */
export async function matchKeys(code: string, buildId: string, romSha1: string): Promise<{ build: string; rom: string }> {
  const h = async (s: string) => {
    const d = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)));
    return [...d].map((b) => b.toString(16).padStart(2, '0')).join('');
  };
  return { build: await h(`rtw-net1|build|${code}|${buildId}`), rom: await h(`rtw-net1|rom|${code}|${romSha1}`) };
}

/** Median and 90th percentile of round-trip samples (ms, rounded). */
export function pingSummary(samples: number[]): { median: number; p90: number } | null {
  if (!samples.length) return null;
  const s = [...samples].sort((a, b) => a - b);
  const at = (q: number) => s[Math.min(s.length - 1, Math.floor(q * s.length))];
  return { median: Math.round(at(0.5)), p90: Math.round(at(0.9)) };
}

/**
 * The boot's pair table: for each pair of room ids, the worse of what each side measured (so both peers read the same
 * numbers). `rows[id]` are the PingRows reported by peer `id` (the room host's own included).
 */
export function pairTable(ids: number[], rows: Map<number, PingRow[]>): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const a = ids[i], b = ids[j];
      const ab = rows.get(a)?.find((r) => r[0] === b), ba = rows.get(b)?.find((r) => r[0] === a);
      const med = Math.max(ab?.[1] ?? -1, ba?.[1] ?? -1), p90 = Math.max(ab?.[2] ?? -1, ba?.[2] ?? -1);
      if (med >= 0 && p90 >= 0) out.push([a, b, med, p90]);
    }
  }
  return out;
}
